import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  boolean,
  jsonb,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const MIGRATION_SOURCES = ["ghl", "activecampaign", "hubspot", "csv"] as const;
export type MigrationSource = (typeof MIGRATION_SOURCES)[number];

export const MIGRATION_STATUSES = ["pending", "mapping", "importing", "validating", "completed", "failed", "rolled_back"] as const;
export type MigrationStatus = (typeof MIGRATION_STATUSES)[number];

export const FIELD_TRANSFORM_TYPES = ["direct", "uppercase", "lowercase", "trim", "default", "regex_extract", "template", "conditional"] as const;
export type FieldTransformType = (typeof FIELD_TRANSFORM_TYPES)[number];

export const CONFLICT_RESOLUTIONS = ["skip", "overwrite", "merge", "manual", "pending"] as const;
export type ConflictResolution = (typeof CONFLICT_RESOLUTIONS)[number];

// ---------------------------------------------------------------------------
// 1. Migration Jobs — Tracks data migration runs from external platforms (F1)
// ---------------------------------------------------------------------------

export const migrationJobs = pgTable("migration_jobs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  source: text("source").notNull(), // MigrationSource
  status: text("status").notNull().default("pending"), // MigrationStatus
  totalRecords: integer("total_records").notNull().default(0),
  importedRecords: integer("imported_records").notNull().default(0),
  failedRecords: integer("failed_records").notNull().default(0),
  skippedRecords: integer("skipped_records").notNull().default(0),
  // errorLog may contain source platform data with PII
  errorLog: jsonb("error_log").$type<Array<Record<string, unknown>>>(),
  piiClassification: jsonb("pii_classification"),
  startedAt: timestamp("started_at", tz),
  completedAt: timestamp("completed_at", tz),
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mig_job_tenant").on(table.tenantId),
  index("idx_mig_job_status").on(table.tenantId, table.status),
]);

// ---------------------------------------------------------------------------
// 2. Migration Field Mappings — Source → target field mappings
// ---------------------------------------------------------------------------

export const migrationFieldMappings = pgTable("migration_field_mappings", {
  id: uuid("id").primaryKey().defaultRandom(),
  migrationJobId: uuid("migration_job_id").notNull(), // AC2: FK to migration_jobs
  tenantId: uuid("tenant_id").notNull(),
  sourceField: text("source_field").notNull(),
  targetField: text("target_field").notNull(),
  transformType: text("transform_type").notNull().default("direct"), // FieldTransformType
  transformConfig: jsonb("transform_config"), // Config for non-direct transforms
  // CQ1: confidence as numeric(3,2) — range 0.00-1.00 for AI-suggested mappings
  confidence: numeric("confidence", { precision: 3, scale: 2 }),
  // EU AI Act: AI suggestions require explicit human approval
  suggestedByAi: boolean("suggested_by_ai").notNull().default(false),
  approvedBy: uuid("approved_by"), // Staff user who approved AI suggestion
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mig_field_tenant").on(table.tenantId),
  index("idx_mig_field_job").on(table.tenantId, table.migrationJobId),
]);

// ---------------------------------------------------------------------------
// 3. Migration Conflicts — Duplicate/conflict records during import
// ---------------------------------------------------------------------------

export const migrationConflicts = pgTable("migration_conflicts", {
  id: uuid("id").primaryKey().defaultRandom(),
  migrationJobId: uuid("migration_job_id").notNull(), // AC2: FK to migration_jobs
  tenantId: uuid("tenant_id").notNull(),
  sourceRecord: jsonb("source_record").notNull(), // The incoming record
  existingRecordId: uuid("existing_record_id"), // ID of the conflicting existing record
  conflictType: text("conflict_type").notNull(), // "duplicate_email" | "duplicate_phone" | "duplicate_name"
  resolution: text("resolution").notNull().default("pending"), // ConflictResolution
  resolvedBy: uuid("resolved_by"), // Staff user who resolved
  resolvedAt: timestamp("resolved_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mig_conflict_tenant").on(table.tenantId),
  index("idx_mig_conflict_job").on(table.tenantId, table.migrationJobId),
  index("idx_mig_conflict_resolution").on(table.tenantId, table.resolution),
]);
